import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { readsMeta } from '@signalvote/data';
import type { SortInboxResponse } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

/**
 * ADR-026 (PR-B) — the paginated close-ledger DISPLAY stream. After the
 * work-vs-display split (ADR-026) this hook feeds ONLY the `TriageLedger`
 * check-register; every other work surface reads the bounded NeedsAction feed
 * ({@link useSortInboxQueue}) or finance-health, never this unbounded display
 * array. Because `TriageLedger` is the sole consumer, this is an in-place
 * conversion of the old single-`useQuery` shape to `useInfiniteQuery`.
 *
 * Fetches keyset-cursor pages of <c>GET /api/finance/sort-inbox</c>
 * (`?limit=100&cursor=…`, page size 100), accumulating them page by page. The
 * cursor is opaque — round-tripped verbatim from the prior page's
 * `nextCursor` into the next request's `?cursor=`, never parsed. A `null`
 * `nextCursor` is the last page (`hasNextPage` becomes false). Append-only: no
 * `getPreviousPageParam` / `maxPages` window (deferred per ADR-026).
 *
 * `staleTime: FINANCE_STALE_TIME` (60 s) keeps a warm close↔elsewhere navigation
 * from re-downloading page 1 within that window. Mutations still refresh the loaded pages
 * via plain `invalidateQueries` on the 2-element
 * {@link queryKeys.finance.sortInboxAll} prefix (ADR-026 — no optimistic
 * infinite-cache patching); that prefix matches this display key, so a
 * treasurer mutation refetches only the pages already loaded (always ≤ the
 * pre-paging full-array cost), and the staleTime never masks a real change. Do
 * NOT narrow those mutation invalidations back to the exact 3-element display
 * key — the NeedsAction feed shares the `['finance','sort-inbox']` prefix.
 *
 * Multi-tenancy: `authService.authenticatedFetch` adds the X-Subsidiary +
 * X-Campaign-Id headers via the existing interceptor; the display key is
 * cid()-scoped so cross-campaign cache stays isolated (distinct from the
 * needsAction feed key).
 *
 * ADR-026 (PR-D) — the optional `filter` (Type kinds + payee/contributor `q`)
 * flows into BOTH the query key (a segment AFTER cid(), so `sortInboxAll()` still
 * prefix-matches it) and the `queryFn` URL (repeated `&kind=` PascalCase wire
 * values + `&q=`). A filter change is a distinct cache entry; a cold key fetches
 * from page 1 (no cursor) with no manual reset. An unfiltered call carries the
 * default filter, so it keys + fetches exactly like the pre-filter display query. The kinds are sorted here so
 * toggling Expenditure-then-Contribution vs the reverse hash to the SAME key (no
 * spurious refetch on toggle order) and the `?kind=` params serialize
 * deterministically — stable for any caller. The sibling `useSortInboxQueue()`
 * feed takes no filter: the bar narrows the display ledger only, never the work
 * queue.
 */
export function useSortInbox(
  filter: { kinds: string[]; q: string } = { kinds: [], q: '' },
) {
  const { selectedCampaign } = useCampaignStore();
  const kinds = [...filter.kinds].sort();
  const q = filter.q;

  return useInfiniteQuery({
    queryKey: queryKeys.finance.sortInbox({ kinds, q }),
    queryFn: async ({ pageParam }) => {
      const cursorParam = pageParam
        ? `&cursor=${encodeURIComponent(pageParam)}`
        : '';
      const kindParams = kinds
        .map((k) => `&kind=${encodeURIComponent(k)}`)
        .join('');
      const qParam = q ? `&q=${encodeURIComponent(q)}` : '';
      return apiJson<SortInboxResponse>(
        `/api/finance/sort-inbox?limit=100${cursorParam}${kindParams}${qParam}`,
        { error: 'Failed to fetch sort inbox' },
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: FINANCE_STALE_TIME,
    // Tab-switch triggers a window-focus refetch by default. The sort-inbox
    // payload can be large (100+ rows); mutations already call invalidateQueries
    // explicitly, so a focus-refetch is redundant and expensive.
    refetchOnWindowFocus: false,
    meta: readsMeta('GET /api/finance/sort-inbox'),
    // ADR-026 (PR-D) — a filter/search change re-keys this query (the filter is a
    // key segment). Without a placeholder that means `data` momentarily goes
    // `undefined`, and `TriageLedger`'s `!hasRows` guard would unmount the WHOLE
    // ledger — filter bar + search input included — flashing the page and
    // dropping input focus mid-keystroke. `keepPreviousData` holds the prior
    // filter's pages as the placeholder until the new ones arrive, so the
    // component stays mounted and the rows just swap in place.
    placeholderData: keepPreviousData,
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    refetchInterval: liveInterval,
  });
}

/**
 * ADR-026 (PR-A) — the bounded NeedsAction WORK feed, split out from the
 * unbounded display ledger above. Fetches
 * <c>GET /api/finance/sort-inbox?status=needsAction</c>: the NeedsAction-only
 * slice, UNPAGINATED (bounded by un-triaged work, not history), plus the
 * envelope scalars (identical values to the display response — the filter
 * changes `items` only). Feeds the TriageModal queue, InboxStrip's counts, and
 * useCloseNavBadge so those work surfaces never derive off a paginated display
 * array that could strand an old-dated NeedsAction row off page 1.
 *
 * Lives under a DISTINCT query key (`sortInboxQueue()`) so it never collides
 * with the display query (`sortInbox()`); both are cid()-scoped so
 * cross-campaign cache stays isolated. The mutation invalidation prefix
 * (`sortInboxAll()`) matches both keys so a mutation refreshes the feed too.
 */
/** A demo view: `?live=1` on the ledger's URL refetches every 5 seconds, so a line recorded elsewhere (by Scout) shows up. */
const liveInterval = (): number | false =>
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('live') === '1' ? 5000 : false;

export function useSortInboxQueue() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.sortInboxQueue(),
    queryFn: () =>
      apiJson<SortInboxResponse>('/api/finance/sort-inbox?status=needsAction', {
        error: 'Failed to fetch sort inbox queue',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: FINANCE_STALE_TIME,
    meta: readsMeta('GET /api/finance/sort-inbox'),
    // Tab-switch triggers a window-focus refetch by default. The NeedsAction
    // feed is refetched by mutations via invalidateQueries; a focus-refetch
    // is redundant and can trigger a ~10 s server-side query on every tab-switch.
    refetchOnWindowFocus: false,
    refetchInterval: liveInterval,
  });
}
